import { c as _c } from "react/compiler-runtime";
import { shouldOfferToManageSeats } from "@components/table/members/assign-seats/assign-seats-utils";
import { MANAGE_SEATS_TITLE, ManageSeatsDialog } from "@components/table/members/assign-seats/manage-seats-dialog";
import { SEAT_CARD_FRAME_SX, SeatCardContent } from "@components/table/members/assign-seats/seat-card-content";
import { useMemberSeats } from "@components/table/members/assign-seats/use-member-seats";
import { STATUS_CHIP_SX } from "@components/table/members/members-column/status-column/member-status";
import { UserInfoSection } from "@components/user-info/user-info-section";
import { Button, Chip, Skeleton } from "@flat-ui";
import { useFetchMemberSeats } from "@hooks/use-fetch-member-seats";
import { useMemberSeatsLookup } from "@hooks/use-member-seats-lookup";
import { Box, Stack } from "@mui-wrapper";
import { areActiveMembersCurrentSelector } from "@store/active-members/active-members-selector";
import { useAppSelector } from "@store/store-helper";
import { DEFAULT_TEXT_FONT_SIZE } from "@styles/common-styles";
import { getPlanSeats, isSeatHeld, isSeatHoldingKnown } from "@utils/member-seats-utils";
import { useState } from "react";

/** The height of a seat card while the seats are loaded, as high as one with a two-line description */
const SKELETON_CARD_HEIGHT = "4.5rem";
/** A chip that tells whether the member holds the seat */
function SeatStatusChip(t0) {
  const $ = _c(4);
  const {
    isHeld
  } = t0;
  const t1 = isHeld ? "Assigned" : "Not assigned";
  const t2 = isHeld ? "success" : "default";
  let t3;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      ...STATUS_CHIP_SX,
      flexShrink: 0
    };
    $[0] = t3;
  } else {
    t3 = $[0];
  }
  let t4;
  if ($[1] !== t1 || $[2] !== t2) {
    t4 = <Chip label={t1} color={t2} sx={t3} />;
    $[1] = t1;
    $[2] = t2;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  return t4;
}

/**
 * Section of the profile card that tells which seats of the workspace subscription the member holds,
 * one card per seat the plan includes. The cards only tell, the seats are changed in the Manage
 * seats dialog, which is only offered to users that could change any of them.
 *
 * Nothing is shown while the plan includes no seat, e.g. for users that may not read the
 * subscriptions of the workspace, since there is nothing to tell then.
 *
 * The dialog stays mounted, since saving it changes the seats the section depends on while the
 * dialog still has to go on, see ManageSeatsDialog.
 */
export function MemberSeatsSection(t0) {
  const $ = _c(21);
  const {
    member
  } = t0;
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const {
    areActiveMembersLoading: isLoading
  } = useFetchMemberSeats();
  const seatsLookup = useMemberSeatsLookup();
  const seats = getPlanSeats(seatsLookup);
  const {
    heldSeats,
    seatOptions
  } = useMemberSeats(member);
  const areActiveMembersKnown = useAppSelector(areActiveMembersCurrentSelector);
  if (!seats.length) {
    return null;
  }
  const T0 = UserInfoSection;
  const T1 = Stack;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      gap: 1
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const t2 = "MEMBER_SEATS_SECTION";
  let t3;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      alignItems: "center",
      justifyContent: "space-between"
    };
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  let t4;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <Box component="div" sx={{
      color: "text.secondary",
      fontSize: DEFAULT_TEXT_FONT_SIZE
    }}>Seats</Box>;
    $[2] = t4;
  } else {
    t4 = $[2];
  }
  let t5;
  if ($[3] !== isLoading || $[4] !== seatOptions) {
    t5 = !isLoading && shouldOfferToManageSeats(seatOptions) && <Button variant="ghost" aria-label={MANAGE_SEATS_TITLE} onClick={() => setIsDialogOpen(true)}>Manage</Button>;
    $[3] = isLoading;
    $[4] = seatOptions;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] !== t5) {
    t6 = <Stack direction="row" sx={t3}>{t4}{t5}</Stack>;
    $[6] = t5;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  const t7 = isLoading ? <Skeleton variant="rounded" data-testid="MEMBER_SEATS_SKELETON" sx={{
    width: "100%",
    height: SKELETON_CARD_HEIGHT
  }} /> : seats.map(seat => <Box key={seat} sx={SEAT_CARD_FRAME_SX} data-testid={`member-seat-card-${seat}`}><SeatCardContent seat={seat} corner={isSeatHoldingKnown(heldSeats, seat, seatsLookup, areActiveMembersKnown) && <SeatStatusChip isHeld={isSeatHeld(heldSeats, seat)} />} /></Box>);
  let t8;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = () => setIsDialogOpen(false);
    $[8] = t8;
  } else {
    t8 = $[8];
  }
  let t9;
  if ($[9] !== isDialogOpen || $[10] !== member) {
    t9 = <ManageSeatsDialog member={member} isOpen={isDialogOpen} onClose={t8} />;
    $[9] = isDialogOpen;
    $[10] = member;
    $[11] = t9;
  } else {
    t9 = $[11];
  }
  let t10;
  if ($[12] !== T1 || $[13] !== t1 || $[14] !== t6 || $[15] !== t7 || $[16] !== t9) {
    t10 = <T1 sx={t1} data-testid={t2}>{t6}{t7}{t9}</T1>;
    $[12] = T1;
    $[13] = t1;
    $[14] = t6;
    $[15] = t7;
    $[16] = t9;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  let t11;
  if ($[18] !== T0 || $[19] !== t10) {
    t11 = <T0>{t10}</T0>;
    $[18] = T0;
    $[19] = t10;
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  return t11;
}